import classNames from 'classnames'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { SessionState } from 'sip.js'
import { CampaignManagerContext } from '~/service/CampaignManager'
import { CallBoth, SipContext } from '~/service/SipConn'
import { FormattedMessage } from '~/translations/utils'
import { SubmitForm } from '../calls/show/Describe/types'
import CallButton from '../common/CallButton'
import Timer, { TimerStatic } from '../common/Timer'
import MainContent from '../MainContent'
import Data from './call/Data'
import Keypad from './call/Keypad'
import Redirect from './call/Redirect'
import Transfer from './call/Transfer'

require('~/utils/sound')

type Props = {
  call: CallBoth
}

export type Tab = 'describe' | 'keypad' | 'transfer' | 'redirect'

const Call: React.FC<Props> = ({ call }) => {
  const { toggleMute, hide } = useContext(SipContext)
  const [startSubmit, setStartSubmit] = useState(false)
  const { activeCampaign, next } = useContext(CampaignManagerContext)

  const [tab, setTab] = useState<Tab | undefined>(
    window.c.itsm ? undefined : 'describe'
  )

  const submitRef = useRef<SubmitForm>()

  useEffect(() => {
    if (call.establishedAt && call.terminatedAt) {
      if (!window.c.itsm) {
        setTab('describe')
      }
    }
  }, [call.terminatedAt])

  const status = (() => {
    switch (call.sessionState) {
      case SessionState.Initial:
        return <span className="fa fa-cog fa-spin" />

      case SessionState.Establishing:
        return <span className="fa fa-spinner-third fa-spin" />

      case SessionState.Established:
        return <Timer initial={call.establishedAt} />

      case SessionState.Terminated:
        if (!call.establishedAt) {
          return null
        }

        return (
          <TimerStatic
            dateA={call.establishedAt}
            dateB={call.terminatedAt as Date}
          />
        )
    }
  })() as JSX.Element

  const onEndClick = async () => {
    if (activeCampaign) {
      await next()
    } else {
      // mogłoby cofać do podstrony, z której uruchomiono dzwonienie
      // ale aktualnie nie ma takiego ficzera
      // history.go(-1)
    }

    hide()
  }

  return (
    <MainContent className="is-whiteBg">
      <div className="phone-call">
        {call.apiData ? (
          <Data
            call={call}
            status={status}
            tab={tab}
            setStartSubmit={setStartSubmit}
            submitRef={submitRef}
            onEndClick={onEndClick}
          />
        ) : (
          <div className="phone-call-top">{status}</div>
        )}

        {tab == 'keypad' && <Keypad call={call} />}
        {tab == 'redirect' && call.isIncoming && <Redirect call={call} />}
        {tab == 'transfer' && call.isIncoming && <Transfer call={call} />}

        <div className="main-content-stickyButton">
          {call.establishedAt && !call.terminatedAt && (
            <div className="phone-call-buttons">
              <button
                className={classNames({ 'is-active': call.muted })}
                onClick={() => toggleMute(call.uuid)}
                disabled={!!call.terminatedAt}
              >
                <span className="far fa-microphone-alt-slash" />
                <FormattedMessage id="phone / call / mute" />
              </button>

              {call.isIncoming && (
                <button
                  className={classNames({ 'is-active': tab == 'transfer' })}
                  onClick={() =>
                    setTab(tab == 'transfer' ? undefined : 'transfer')
                  }
                  disabled={!!call.terminatedAt}
                >
                  <span className="far fa-user-headset" />
                  <FormattedMessage id="phone / call / transfer" />
                </button>
              )}

              {call.isIncoming && call.apiData && call.apiData.canRedirect && (
                <button
                  className={classNames({ 'is-active': tab == 'redirect' })}
                  onClick={() =>
                    setTab(tab == 'redirect' ? undefined : 'redirect')
                  }
                  disabled={!!call.terminatedAt}
                >
                  <span className="far fa-share" />
                  <FormattedMessage id="phone / call / redirect" />
                </button>
              )}

              <button
                className={classNames({ 'is-active': tab == 'keypad' })}
                onClick={() => setTab(tab == 'keypad' ? undefined : 'keypad')}
                disabled={!!call.terminatedAt}
              >
                <span className="far fa-phone-office" />
                <FormattedMessage id="phone / call / keypad" />
              </button>

              {!window.c.itsm && (
                <button
                  className={classNames({ 'is-active': tab == 'describe' })}
                  onClick={() =>
                    setTab(tab == 'describe' ? undefined : 'describe')
                  }
                >
                  <span className="far fa-pencil" />
                  <FormattedMessage id="phone / call / describe" />
                </button>
              )}
            </div>
          )}

          {/* {!call.terminatedAt && (
            <CallButton
              onClick={
                call.isIncoming || call.establishedAt ? call.bye : call.cancel
              }
              reject
            />
          )} */}

          <EndButton
            call={call}
            submitRef={submitRef}
            disabled={startSubmit}
            onEndClick={onEndClick}
          />
        </div>
      </div>
    </MainContent>
  )
}

export default Call

//

type EndButtonProps = {
  call: CallBoth
  submitRef: React.MutableRefObject<SubmitForm | undefined>
  disabled: boolean
  onEndClick: () => Promise<void>
}

const EndButton: React.FC<EndButtonProps> = ({
  call,
  submitRef,
  disabled,
  onEndClick,
}) => {
  const { activeCampaign } = useContext(CampaignManagerContext)

  if (!call.terminatedAt) {
    return (
      <CallButton
        onClick={call.isIncoming || call.establishedAt ? call.bye : call.cancel}
        reject
      />
    )
  }

  const onClick = async () => {
    if (submitRef.current) {
      // jest Describe po udanej rozmowie
      submitRef?.current()
    } else {
      // brak formularza, bo rozmowa nieudana, więc od razu ukrywamy
      onEndClick()
    }
  }

  if (activeCampaign) {
    return <CallButton next disabled={disabled} onClick={onClick} />
  }

  return <CallButton save disabled={disabled} onClick={onClick} />
}
